import { useState, type DragEvent, type KeyboardEvent, type ReactNode } from "react";
import { useIntl } from "react-intl";
import { SKILL_LEVELS, fromStoragePercent, toStoragePercent } from "@/lib/skill-levels";
import { skillLevelBandBackground, skillLevelSwatch } from "@/lib/skill-level-style";
import { useThemePrefs } from "@/stores/theme-prefs";
import { EntryFields, type EntryFieldValues } from "@/components/design/EntryFields";
import { EditPanelButtons } from "@/components/design/EditPanelButtons";
import { SkillLevelInfoIcon } from "@/components/design/SkillLevelInfoIcon";
import { isFieldMissing, type FieldDef } from "@/lib/field-util";
import { useAutoCloseOnSave } from "@/lib/use-auto-close-on-save";
import type { ExpertiseEntry } from "@/pages/save/profile/types";

// Fachwissen "Diagramm" view (#51): one shared vertical axis of the 7 skill
// levels (7 on top -> 1 at the bottom), each level rendered with the same
// signal/neutral bands as the SkillLevelScale editor. Skills sit as tags on their level
// row; skills without a level (percent 0/empty) collect in an "Unrated" row at
// the bottom. Each level is a faint band so a wrapped multi-line block still
// reads as one level.
//
// Editable (own profile): tags are draggable and every band is a drop zone —
// dropping a tag onto a level moves it there (1..7, or 0 = unrated). Clicking a
// tag opens the same EntryFields editor the list view uses. Both persist via
// the page's patchEntry → SaveBar (ADR-0005), not a separate mutation path.
// Native HTML5 DnD, mirroring ServicePage; keyboard users edit via the list.

const UNRATED = 0;

function SkillTag({
  label,
  draggable,
  dragging,
  onDragStart,
  onDragEnd,
  onClick,
}: {
  label: string;
  draggable?: boolean;
  dragging?: boolean;
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: () => void;
  onClick?: () => void;
}) {
  const className = `group/tag inline-flex max-w-full items-center truncate rounded-full px-2 py-0.5 text-[11px] text-ink/80 ring-1 ring-ink/15 transition ${
    onClick
      ? "cursor-grab hover:text-accent hover:ring-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 active:cursor-grabbing"
      : ""
  } ${dragging ? "opacity-40" : ""}`;
  if (onClick) {
    return (
      <span
        role="button"
        tabIndex={0}
        draggable={draggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={onClick}
        onKeyDown={(e: KeyboardEvent<HTMLSpanElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        className={className}
      >
        {label || "–"}
      </span>
    );
  }
  return (
    <span
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={className}
    >
      {label || "–"}
    </span>
  );
}

function SkillEditor({
  values,
  editFields,
  shareControl,
  isDraft,
  onChange,
  onClose,
  onCancel,
  onDelete,
}: {
  values: EntryFieldValues;
  editFields: readonly FieldDef[];
  shareControl?: ReactNode;
  isDraft: boolean;
  onChange: (key: string, value: string | boolean) => void;
  onClose: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
}) {
  const intl = useIntl();
  const missing = editFields.filter((f) => isFieldMissing(f, values[f.key])).map((f) => f.label);
  return (
    <div className="mt-3 space-y-3 rounded-md bg-page p-3 ring-1 ring-ink/10">
      {shareControl ? <div className="flex justify-end">{shareControl}</div> : null}
      <EntryFields
        fields={editFields}
        values={values}
        onChange={onChange}
      />
      <EditPanelButtons
        onCancel={onCancel ?? onClose}
        onConfirm={onClose}
        onDelete={onDelete}
        confirmLabel={
          isDraft
            ? intl.formatMessage({ id: "common.add", defaultMessage: "Add" })
            : intl.formatMessage({ id: "profile.rows.done", defaultMessage: "Done" })
        }
        missingFields={missing}
      />
    </div>
  );
}

export function SkillsDiagram({
  entries,
  editable = false,
  onMove,
  onAdd,
  onPatch,
  onCancel,
  onDelete,
  editFields,
  renderShareControl,
  renderNewShareControl,
  draftIds,
}: {
  entries: ExpertiseEntry[];
  editable?: boolean;
  // Drop callback: target level is 1..7, or 0 to unrate.
  onMove?: (id: string, level: number) => void;
  // Commit a new skill. Deferred: the "+" only opens a transient local editor
  // (no page-level draft, so the SaveBar stays hidden) and this fires once on
  // the editor's "Add" — mirroring the list/AddForm flow for Career/Education.
  onAdd?: (payload: Record<string, unknown>) => void;
  onPatch?: (id: string, patch: Record<string, unknown>) => void;
  onCancel?: (id: string) => void;
  onDelete?: (id: string) => void;
  editFields?: readonly FieldDef[];
  renderShareControl?: (entry: ExpertiseEntry) => ReactNode;
  // Share control for the not-yet-committed new skill, wired to local state
  // (the draft has no entry id until it is added).
  renderNewShareControl?: (shared: boolean, onChange: (shared: boolean) => void) => ReactNode;
  draftIds?: ReadonlySet<string>;
}) {
  const intl = useIntl();
  const accentColor = useThemePrefs((s) => s.accentColor);
  const interactive = editable && Boolean(onPatch);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropLevel, setDropLevel] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Transient new-skill editor, prefilled to the band's level. Lives only here
  // until the user confirms "Add" — that's when onAdd creates the page draft
  // (and the SaveBar appears). Cancel just drops it, touching nothing.
  const [newSkill, setNewSkill] = useState<{ values: Record<string, unknown>; share: boolean } | null>(null);
  // Same SaveBar-tick subscription as the list rows: close the editor on a
  // successful commit (partial failures keep it open).
  useAutoCloseOnSave(() => setEditingId(null));

  // Bucket skills by level (1..7); percent 0/empty -> unrated.
  const byLevel = new Map<number, ExpertiseEntry[]>();
  for (const e of entries) {
    // Hide a brand-new, still-unnamed draft: an empty "–" tag only confuses.
    // It surfaces once it has a description (kept on Add, dropped on Cancel via
    // the editor). The editor still binds to it while it's hidden.
    const isDraft = draftIds?.has(e.id) ?? e.id.startsWith("draft-");
    if (isDraft && !e.description?.trim()) continue;
    const lvl = fromStoragePercent(Number(e.level));
    const bucket = byLevel.get(lvl);
    if (bucket) bucket.push(e);
    else byLevel.set(lvl, [e]);
  }
  const unrated = byLevel.get(UNRATED) ?? [];
  const editingEntry = editingId ? entries.find((e) => e.id === editingId) ?? null : null;

  const tagProps = (e: ExpertiseEntry) =>
    interactive
      ? {
          draggable: true,
          dragging: draggingId === e.id,
          onClick: () => {
            setNewSkill(null);
            setEditingId((id) => (id === e.id ? null : e.id));
          },
          onDragStart: (ev: DragEvent) => {
            ev.dataTransfer.effectAllowed = "move";
            ev.dataTransfer.setData("text/plain", e.id);
            setDraggingId(e.id);
          },
          onDragEnd: () => {
            setDraggingId(null);
            setDropLevel(null);
          },
        }
      : {};

  const dropHandlers = (level: number) =>
    interactive
      ? {
          onDragOver: (e: DragEvent) => {
            if (!draggingId) return;
            e.preventDefault();
            if (dropLevel !== level) setDropLevel(level);
          },
          onDragLeave: () => setDropLevel((d) => (d === level ? null : d)),
          onDrop: (e: DragEvent) => {
            e.preventDefault();
            const id = e.dataTransfer.getData("text/plain") || draggingId;
            if (id) onMove?.(id, level);
            setDropLevel(null);
            setDraggingId(null);
          },
        }
      : {};

  return (
    <div>
      <div className="space-y-1">
        {[...SKILL_LEVELS].reverse().map((l) => {
          const swatch = skillLevelSwatch(l.value, accentColor);
          const levelName = intl.formatMessage({ id: l.nameId, defaultMessage: l.name });
          const skills = byLevel.get(l.value) ?? [];
          return (
            <div
              key={l.value}
              {...dropHandlers(l.value)}
              className={`group/level flex items-center gap-3 rounded-md px-2 py-1.5 transition ${
                dropLevel === l.value ? "ring-2 ring-accent/60" : ""
              }`}
              style={{ background: skillLevelBandBackground(l.value) }}
            >
              <div className="flex w-36 shrink-0 items-center gap-2">
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-medium tabular-nums ring-1 ring-ink/10"
                  style={{
                    background: swatch.background,
                    color: swatch.textColor,
                  }}
                >
                  {l.value}
                </span>
                <span className="min-w-0 truncate text-[11px] text-ink/70">{levelName}</span>
                <SkillLevelInfoIcon
                  level={l}
                  className="shrink-0 opacity-0 transition group-hover/level:opacity-100 group-focus-within/level:opacity-100"
                />
              </div>
              <div className="flex min-h-6 flex-1 flex-wrap items-center gap-1.5">
                {skills.map((e) => (
                  <SkillTag key={e.id} label={e.description} {...tagProps(e)} />
                ))}
              </div>
              {interactive && onAdd ? (
                <button
                  type="button"
                  aria-label={intl.formatMessage({
                    id: "profile.skills.addAtLevel",
                    defaultMessage: "Add skill at this level",
                  })}
                  onClick={() => {
                    setEditingId(null);
                    setNewSkill({ values: { level: toStoragePercent(l.value) }, share: true });
                  }}
                  className="shrink-0 rounded-full p-1 text-ink/40 opacity-0 transition hover:text-accent focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ink/30 group-hover/level:opacity-100"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              ) : null}
            </div>
          );
        })}

        {unrated.length > 0 || interactive ? (
          <div
            {...dropHandlers(UNRATED)}
            className={`mt-1 flex items-center gap-3 rounded-md px-2 py-1.5 transition ${
              dropLevel === UNRATED ? "ring-2 ring-accent/60" : ""
            }`}
          >
            <div className="w-36 shrink-0 text-[11px] text-ink/45">
              {intl.formatMessage({ id: "profile.skills.unrated", defaultMessage: "Unrated" })}
            </div>
            <div className="flex min-h-6 flex-1 flex-wrap items-center gap-1.5">
              {unrated.map((e) => (
                <SkillTag key={e.id} label={e.description} {...tagProps(e)} />
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {interactive && newSkill && onAdd && editFields ? (
        <SkillEditor
          key="__new__"
          values={newSkill.values as EntryFieldValues}
          editFields={editFields}
          shareControl={
            renderNewShareControl
              ? renderNewShareControl(newSkill.share, (v) =>
                  setNewSkill((s) => (s ? { ...s, share: v } : s)),
                )
              : undefined
          }
          isDraft
          onChange={(key, val) =>
            setNewSkill((s) => (s ? { ...s, values: { ...s.values, [key]: val } } : s))
          }
          onClose={() => {
            onAdd({ ...newSkill.values, sharedPublic: newSkill.share });
            setNewSkill(null);
          }}
          onCancel={() => setNewSkill(null)}
        />
      ) : interactive && editingEntry && onPatch && editFields ? (
        <SkillEditor
          key={editingEntry.id}
          values={editingEntry as unknown as EntryFieldValues}
          editFields={editFields}
          shareControl={renderShareControl?.(editingEntry)}
          isDraft={draftIds?.has(editingEntry.id) ?? editingEntry.id.startsWith("draft-")}
          onChange={(key, val) => onPatch(editingEntry.id, { [key]: val })}
          onClose={() => setEditingId(null)}
          onCancel={
            onCancel
              ? () => {
                  onCancel(editingEntry.id);
                  setEditingId(null);
                }
              : undefined
          }
          onDelete={
            onDelete && !(draftIds?.has(editingEntry.id) ?? editingEntry.id.startsWith("draft-"))
              ? () => {
                  onDelete(editingEntry.id);
                  setEditingId(null);
                }
              : undefined
          }
        />
      ) : null}
    </div>
  );
}
